import { Box, InputBase, SxProps, Theme } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { IconButtonOutlined } from "./IconButtonOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import Button from "@mui/material/Button";
import React, { useEffect, useState } from "react";
import { makeStyles } from "@mui/styles";

const useStyle = makeStyles((theme) => ({
  searchIcon: {
    width: "16px",
    height: "16px",
    fontWeight: "bold",
  },
  filterModal: {
    position: "absolute",
    width: "900px",
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
  searchBar: {
    "&:hover": {
      borderColor: theme.palette.primary.main,
    },
  },
}));

interface SearchBarProps {
  placeholder: string;
  searchText: string;
  onSubmit: (searchQuery: string) => void;
  onInputChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  HandleDataLayer: (event: string) => void;
  hideSearchButton?: boolean;
  sx?: SxProps<Theme> | undefined;
}

export const SearchBar = (props: SearchBarProps) => {
  const classes = useStyle();

  const [currentSearchText, setCurrentSearchText] = useState("");

  const [searchBarFocused, setSearchBarFocused] = useState(false);
  const onFocused = () => setSearchBarFocused(true);
  const onBlurred = () => setSearchBarFocused(false);
  const {
    searchText,
    onSubmit,
    onInputChange,
    HandleDataLayer,
    placeholder,
    hideSearchButton = false,
  } = props;

  let submitSearch = () => {
    onSubmit(currentSearchText);
  };

  let keyPress = (e: any) => {
    if (e.keyCode === 13) {
      submitSearch();
    }
  };

  useEffect(() => {
    if (searchText === "") {
      setCurrentSearchText("");
    } else {
      setCurrentSearchText(searchText);
    }
  }, [searchText]);

  return (
    <>
      <Box
        className={classes.searchBar}
        sx={{
          maxWidth: "448px",
          flexGrow: 1,
          backgroundColor: "#ffffff",
          height: "37px",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: "8px",
          padding: "8px",
          borderRadius: "15px",
          border: "2px solid #DEDEDE",
          ...props.sx,
        }}>
        <SearchIcon sx={{ color: "#000", fontSize: "16px", margin: "3px 0px 3px 8px" }} />
        <InputBase
          onKeyDown={keyPress}
          onFocus={onFocused}
          onBlur={onBlurred}
          value={currentSearchText}
          onChange={(e) => {
            setCurrentSearchText(e.target.value);
            onInputChange(e);
          }}
          sx={{ ml: 1, flex: 1 }}
          placeholder={placeholder}
          inputProps={{ "aria-label": placeholder }}
        />
        {currentSearchText.length > 0 ? (
          <IconButtonOutlined
            sx={{
              width: "21px",
              height: "21px",
              border: "2px solid #5D5D5D !important",
              "&:hover": {
                border: "2px solid #EE4069 !important",
                backgroundColor: "transparent !important",
              },
            }}
            hover={
              <CloseRoundedIcon
                style={{ width: "16px", height: "16px", color: "#EE4069" }}
              />
            }
            tooltipLabel="Clear current search"
            tooltipPlacement="bottom"
            onClick={() => {
              setCurrentSearchText("");
              onSubmit("");
              HandleDataLayer("close cross");
              onInputChange({ target: { value: "" } } as any);
            }}>
            <CloseRoundedIcon
              style={{ width: "16px", height: "16px", color: "#5D5D5D" }}
            />
          </IconButtonOutlined>
        ) : null}
      </Box>
      {!hideSearchButton && (searchBarFocused || currentSearchText.length > 0) && (
        <Button
          onClick={() => {
            submitSearch();
            HandleDataLayer("search");
          }}
          sx={{ fontSize: "0.875rem" }}
          size="small"
          variant="contained"
          startIcon={<SearchIcon className={classes.searchIcon} />}>
          Search
        </Button>
      )}
    </>
  );
};
